import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, MapPin, Plus, Mic, Square, CheckCircle2, Send, Headset, X, CalendarDays } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { CATS, CAT_LABEL, PRIO, STATUS_CLS, fmtDT, newClientId, Attachment, Timeline } from "./shared";

const card = "rounded-2xl border border-[#E9C46A]/25 bg-[#0B0A08]";
const Top = ({ title, sub, onBack }) => (
  <div className="flex items-center gap-2 mb-4">
    <button type="button" onClick={onBack} className="w-10 h-10 flex items-center justify-center text-slate-200" data-testid="office-back"><ChevronLeft size={22} /></button>
    <div><div className="text-white font-display text-xl font-semibold">{title}</div>{sub && <div className="text-xs text-slate-400">{sub}</div>}</div>
  </div>
);

function Form({ cat, onDone, onBack }) {
  const [svcs, setSvcs] = useState([]);
  const [bid, setBid] = useState("");
  const [desc, setDesc] = useState("");
  const [prio, setPrio] = useState("normale");
  const [loc, setLoc] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(null);
  const cid = useRef(newClientId());
  useEffect(() => { api.get("/driver-requests/my-services").then(r => setSvcs(r.data)).catch(() => {}); }, []);
  const svc = svcs.find(s => s.id === bid);
  const addFiles = (list) => {
    const arr = [...files, ...Array.from(list)].slice(0, 5);
    const bad = arr.find(f => f.size > 25 * 1024 * 1024);
    if (bad) { toast.error(`${bad.name}: oltre 25 MB`); return; }
    setFiles(arr);
  };
  const getLoc = () => {
    if (!navigator.geolocation) { toast.error("Posizione non supportata da questo browser"); return; }
    navigator.geolocation.getCurrentPosition(p => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => toast.error("Permesso posizione negato: puoi inviare la richiesta anche senza"), { enableHighAccuracy: true, timeout: 15000 });
  };
  const toggleRec = async () => {
    if (rec) { rec.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); const chunks = [];
      mr.ondataavailable = e => chunks.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach(t => t.stop()); setRec(null); const type = mr.mimeType || "audio/webm";
        addFiles([new File(chunks, `audio-${Date.now()}.${type.includes("mp4") ? "m4a" : "webm"}`, { type: type.split(";")[0] })]); };
      mr.start(); setRec(mr);
    } catch { toast.error("Microfono non disponibile o permesso negato"); }
  };
  const send = async () => {
    if (desc.trim().length < 3) { toast.error("Scrivi una descrizione"); return; }
    setBusy(true);
    try {
      const r = await api.post("/driver-requests", { client_id: cid.current, category: cat, priority: prio, description: desc.trim(), booking_id: bid || null, location: loc });
      let failed = 0;
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        try { await api.post(`/driver-requests/${r.data.id}/attachments`, fd); } catch { failed += 1; }
      }
      if (failed) toast.error(`${failed} allegat${failed === 1 ? "o" : "i"} non caricat${failed === 1 ? "o" : "i"}`);
      onDone(r.data);
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="office-form">
      <Top title="Nuova richiesta" sub={CAT_LABEL[cat]} onBack={onBack} />
      <div className={`${card} p-3`}>
        <div className="text-[11px] text-[#E9C46A] mb-1.5 inline-flex items-center gap-1"><CalendarDays size={13} />Servizio collegato</div>
        <select value={bid} onChange={e => setBid(e.target.value)} className="w-full h-11 rounded-lg bg-black border border-white/15 text-white text-sm px-2" data-testid="office-form-service">
          <option value="">Nessun servizio (richiesta generica)</option>
          {svcs.map(s => <option key={s.id} value={s.id}>{s.date?.split("-").reverse().join("/")} {s.time} · {s.pickup} → {s.destination}</option>)}
        </select>
        {svc && <div className="text-xs text-slate-300 mt-2" data-testid="office-form-service-info">{svc.date?.split("-").reverse().join("/")} ore {svc.time} · {svc.pickup} → {svc.destination}{svc.customer ? ` · Cliente: ${svc.customer}` : ""}</div>}
      </div>
      <div>
        <div className="text-sm text-white font-semibold mb-1.5">Descrizione *</div>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={4} maxLength={2000} placeholder="Descrivi la situazione" className="w-full rounded-xl bg-white text-black p-3 text-sm" data-testid="office-form-desc" />
      </div>
      <div>
        <div className="text-sm text-white font-semibold mb-1.5">Priorità</div>
        <div className="grid grid-cols-3 gap-2">{Object.entries(PRIO).map(([k, [l, dot]]) => (
          <button key={k} type="button" onClick={() => setPrio(k)} data-testid={`office-prio-${k}`} className={`h-11 rounded-xl border text-sm inline-flex items-center justify-center gap-2 ${prio === k ? "border-[#E9C46A] bg-[#E9C46A]/15 text-white" : "border-white/15 text-slate-300"}`}><i className={`w-2.5 h-2.5 rounded-full ${dot}`} />{l}</button>
        ))}</div>
      </div>
      <div className={`${card} p-3 flex items-center gap-3`}>
        <MapPin className="text-[#E9C46A] shrink-0" size={20} />
        <div className="flex-1 text-xs text-slate-300" data-testid="office-form-loc">{loc ? `Posizione acquisita (±${Math.round(loc.accuracy || 0)} m)` : "Posizione attuale non inclusa"}</div>
        {loc ? <button type="button" onClick={() => setLoc(null)} className="text-xs text-slate-400 underline">Rimuovi</button>
          : <button type="button" onClick={getLoc} className="h-9 px-3 rounded-lg border border-white/25 text-xs text-white" data-testid="office-form-useloc">Usa posizione</button>}
      </div>
      <div>
        <div className="text-sm text-white font-semibold mb-1.5">Allegati (foto, video, audio) · max 5 da 25 MB</div>
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg border border-white/15 bg-white/5 flex items-center justify-center text-[10px] text-slate-300 p-1 text-center overflow-hidden">
              {f.type.startsWith("image/") ? <img src={URL.createObjectURL(f)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : f.name}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center"><X size={12} /></button>
            </div>
          ))}
          {files.length < 5 && <label className="w-20 h-20 rounded-lg border border-dashed border-white/25 flex items-center justify-center text-slate-300 cursor-pointer" data-testid="office-form-add-file">
            <Plus size={22} /><input type="file" multiple accept="image/*,video/*,audio/*" className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ""; }} data-testid="office-form-file-input" /></label>}
          {files.length < 5 && window.MediaRecorder && <button type="button" onClick={toggleRec} data-testid="office-form-rec" className={`w-20 h-20 rounded-lg border flex flex-col items-center justify-center gap-1 text-[10px] ${rec ? "border-rose-500 text-rose-300 animate-pulse" : "border-white/25 text-slate-300"}`}>{rec ? <Square size={18} /> : <Mic size={18} />}{rec ? "Stop" : "Audio"}</button>}
        </div>
      </div>
      <button type="button" disabled={busy || !!rec} onClick={send} className="w-full h-14 rounded-xl bg-gradient-to-r from-[#E9C46A] to-[#C9982E] text-black font-bold text-base disabled:opacity-50" data-testid="office-form-send">{busy ? "Invio in corso…" : "Invia richiesta"}</button>
      <p className="text-[11px] text-slate-500 text-center">Non è un servizio di emergenza: in caso di pericolo chiama il 112.</p>
    </div>
  );
}

function Detail({ id, onBack }) {
  const [r, setR] = useState(null);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/driver-requests/${id}`).then(x => setR(x.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [id]); // eslint-disable-line
  if (!r) return <div className="text-slate-400 text-sm p-6">Caricamento…</div>;
  const open = ["Da gestire", "In lavorazione"].includes(r.status);
  const send = async () => {
    if (!txt.trim()) return;
    setBusy(true);
    try { await api.post(`/driver-requests/${id}/messages`, { text: txt.trim(), client_id: newClientId() }); setTxt(""); await load(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="office-detail">
      <Top title="Dettaglio richiesta" onBack={onBack} />
      <div className="flex items-center justify-between gap-2"><span className="font-mono-num text-white" data-testid="office-detail-code">#{r.code}</span><span className={`px-2.5 py-1 rounded-md text-xs font-bold ${STATUS_CLS[r.status]}`} data-testid="office-detail-status">{r.status}</span></div>
      {r.booking && <div className={`${card} p-3 text-xs text-slate-300`}>{r.booking.date?.split("-").reverse().join("/")} ore {r.booking.time} · {r.booking.pickup} → {r.booking.destination}</div>}
      <Timeline req={r} office={false} />
      {!!(r.attachments || []).length && <div className="flex flex-wrap gap-2">{r.attachments.map(a => <Attachment key={a.id} rid={r.id} a={a} />)}</div>}
      {r.close_reason && !open && <div className="text-xs text-slate-300">Motivazione: {r.close_reason}</div>}
      {open ? (
        <div className="flex gap-2 sticky bottom-20">
          <input value={txt} onChange={e => setTxt(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Scrivi un messaggio…" className="flex-1 h-12 rounded-xl bg-black border border-white/15 text-white px-3 text-sm" data-testid="office-detail-input" />
          <button type="button" disabled={busy} onClick={send} className="w-12 h-12 rounded-xl bg-[#E9C46A] text-black flex items-center justify-center disabled:opacity-50" data-testid="office-detail-send"><Send size={18} /></button>
        </div>
      ) : <div className="text-xs text-slate-500 text-center" data-testid="office-detail-closed">Richiesta chiusa: resta nello storico.</div>}
    </div>
  );
}

export function DriverOffice({ initialId, onClose }) {
  const [view, setView] = useState(initialId ? { v: "detail", id: initialId } : { v: "list" });
  const [list, setList] = useState([]);
  const loadList = () => api.get("/driver-requests", { params: { page_size: 50 } }).then(r => setList(r.data.items)).catch(() => {});
  useEffect(() => { if (view.v === "list") { loadList(); const t = setInterval(loadList, 15000); return () => clearInterval(t); } }, [view.v]); // eslint-disable-line
  return (
    <div className="fixed inset-0 z-[80] bg-[#050505] overflow-y-auto pt-safe" data-testid="driver-office">
      <div className="max-w-xl mx-auto px-4 py-4 pb-28">
        {view.v === "list" && (<div className="space-y-4">
          <Top title="Ho bisogno dell'ufficio" sub="Le tue richieste all'ufficio" onBack={onClose} />
          <button type="button" onClick={() => setView({ v: "cats" })} className="w-full h-14 rounded-xl bg-gradient-to-r from-[#E9C46A] to-[#C9982E] text-black font-bold inline-flex items-center justify-center gap-2" data-testid="office-new"><Plus size={18} />Nuova richiesta</button>
          {list.map(r => (
            <button key={r.id} type="button" onClick={() => setView({ v: "detail", id: r.id })} className={`${card} w-full p-3 text-left flex items-center gap-3`} data-testid={`office-item-${r.id}`}>
              <span className="min-w-0 flex-1"><span className="block text-white text-sm font-semibold">{CAT_LABEL[r.category]}</span><span className="block text-[11px] text-slate-400 truncate">#{r.code} · {fmtDT(r.created_at)} · agg. {fmtDT(r.updated_at)}</span></span>
              <span className={`px-2 py-1 rounded-md text-[11px] font-bold ${STATUS_CLS[r.status]}`}>{r.status}</span>
            </button>
          ))}
          {!list.length && <div className="text-sm text-slate-500 text-center py-8" data-testid="office-empty">Nessuna richiesta inviata</div>}
        </div>)}
        {view.v === "cats" && (<div>
          <Top title="Ho bisogno dell'ufficio" sub="Seleziona il motivo della richiesta" onBack={() => setView({ v: "list" })} />
          <div className="space-y-2">{CATS.map(([k, l, s, I]) => (
            <button key={k} type="button" onClick={() => setView({ v: "form", cat: k })} className={`${card} w-full p-3.5 flex items-center gap-3 text-left`} data-testid={`office-cat-${k}`}>
              <span className="w-11 h-11 rounded-xl bg-[#E9C46A]/15 border border-[#E9C46A]/40 flex items-center justify-center shrink-0"><I size={20} className="text-[#E9C46A]" /></span>
              <span className="flex-1"><span className="block text-white text-sm font-semibold">{l}</span><span className="block text-[11px] text-slate-400">{s}</span></span><ChevronRight size={18} className="text-slate-500" />
            </button>))}</div>
        </div>)}
        {view.v === "form" && <Form cat={view.cat} onBack={() => setView({ v: "cats" })} onDone={(r) => setView({ v: "sent", r })} />}
        {view.v === "sent" && (
          <div className="text-center pt-10 space-y-5" data-testid="office-sent">
            <CheckCircle2 size={96} className="mx-auto text-[#E9C46A]" strokeWidth={1.4} />
            <div className="text-2xl text-white font-display">Richiesta <span className="text-[#E9C46A]">inviata</span></div>
            <p className="text-sm text-slate-300">La tua richiesta è stata inviata all'ufficio ed è in attesa di essere gestita.</p>
            <div className={`${card} p-4 text-left space-y-3`}>
              <div><div className="text-[11px] text-slate-400">ID richiesta</div><div className="text-white font-mono-num text-lg" data-testid="office-sent-code">#{view.r.code}</div></div>
              <div><div className="text-[11px] text-slate-400">Stato attuale</div><div className="text-white" data-testid="office-sent-status">{view.r.status}</div></div>
            </div>
            <button type="button" onClick={() => setView({ v: "detail", id: view.r.id })} className="w-full h-12 rounded-xl border border-[#E9C46A]/60 text-white" data-testid="office-sent-view">Vedi richiesta</button>
            <button type="button" onClick={onClose} className="w-full h-12 rounded-xl border border-white/20 text-slate-200" data-testid="office-sent-home">Torna alla home</button>
          </div>
        )}
        {view.v === "detail" && <Detail id={view.id} onBack={() => setView({ v: "list" })} />}
      </div>
    </div>
  );
}

export function OfficeTile({ onOpen }) {
  return (
    <button type="button" onClick={onOpen} data-testid="home-office-tile" className="w-full rounded-2xl border border-[#E9C46A]/50 bg-gradient-to-r from-[#1A1408] to-[#0B0A08] p-4 flex items-center gap-4 text-left shadow-[0_0_24px_-10px_rgba(233,196,106,0.6)]">
      <span className="w-12 h-12 rounded-xl bg-[#E9C46A]/15 border border-[#E9C46A]/50 flex items-center justify-center shrink-0"><Headset size={24} className="text-[#E9C46A]" /></span>
      <span className="flex-1"><span className="block text-white font-bold">Ho bisogno dell'ufficio</span><span className="block text-xs text-slate-400">Cliente assente, pagamento, veicolo, ritardo e altro</span></span>
      <ChevronRight size={20} className="text-[#E9C46A]" />
    </button>
  );
}
